import { getStoredLegacyToken } from './legacyAuthService';

export interface PodcastPlaylist {
  id: string;
  name: string;
  description?: string;
  coverUrl?: string;
  isPublished: boolean;
  experienceId?: string;
  experienceName?: string;
  ownerUsername?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface AudioguideItem {
  id: string;
  title: string;
  description: string;
  playlistId?: string;
  playlistName: string;
  playlist?: string;
  audioUrl: string;
  episodeCoverUrl: string;
  coverUrl?: string;
  playlistCoverUrl?: string;
  deepLink: string;
  publicUrl: string;
  qrDataUrl?: string;
  isPublished: boolean;
  experienceId?: string;
  experienceName?: string;
  ownerUsername?: string;
  durationSeconds?: number | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface AudioguidePayload {
  title: string;
  description: string;
  playlistId?: string;
  playlistName: string;
  experienceId?: string;
  experienceName?: string;
  audioUrl: string;
  episodeCoverUrl: string;
  playlistCoverUrl?: string;
  isPublished: boolean;
  durationSeconds?: number | null;
}

export interface PlaylistPayload {
  name: string;
  description?: string;
  coverUrl?: string;
  isPublished: boolean;
  experienceId: string;
  experienceName?: string;
}

type AudioguidesCatalogResponse = {
  success: boolean;
  episodes?: AudioguideItem[];
  playlists?: PodcastPlaylist[];
  error?: string;
};

type GenericResponse = {
  success: boolean;
  id?: string;
  playlist?: PodcastPlaylist;
  episode?: AudioguideItem;
  error?: string;
};

function buildHeaders(): HeadersInit {
  const token = getStoredLegacyToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  }
  return headers;
}

function buildLinks(id: string): { deepLink: string; publicUrl: string } {
  const safeId = encodeURIComponent(String(id || '').trim());
  return {
    deepLink: `glooci://oauthredirect/podcast/${safeId}?podcastId=${safeId}`,
    publicUrl: `https://geniusloci-4f597.web.app/podcast/${safeId}?podcastId=${safeId}`,
  };
}

async function requestJson<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...buildHeaders(),
      ...(init.headers || {}),
    },
  });

  const raw = await response.text();
  let parsed: any = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    throw new Error(parsed?.error || parsed?.message || raw || `Errore ${response.status}`);
  }

  return (parsed || {}) as T;
}

function normalizePlaylist(item: PodcastPlaylist): PodcastPlaylist {
  return {
    id: String(item.id || ''),
    name: String(item.name || '').trim(),
    description: String(item.description || '').trim(),
    coverUrl: String(item.coverUrl || '').trim(),
    isPublished: Boolean(item.isPublished),
    experienceId: String(item.experienceId || '').trim(),
    experienceName: String(item.experienceName || '').trim(),
    ownerUsername: String(item.ownerUsername || '').trim(),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

function normalizeEpisode(item: AudioguideItem): AudioguideItem {
  const links = buildLinks(String(item.id || ''));
  return {
    id: String(item.id || ''),
    title: String(item.title || '').trim(),
    description: String(item.description || '').trim(),
    playlistId: String(item.playlistId || '').trim(),
    playlistName: String(item.playlistName || item.playlist || '').trim(),
    playlist: String(item.playlist || item.playlistName || '').trim(),
    audioUrl: String(item.audioUrl || '').trim(),
    episodeCoverUrl: String(item.episodeCoverUrl || item.coverUrl || '').trim(),
    coverUrl: String(item.coverUrl || '').trim(),
    playlistCoverUrl: String(item.playlistCoverUrl || '').trim(),
    deepLink: String(item.deepLink || links.deepLink).trim(),
    publicUrl: String(item.publicUrl || links.publicUrl).trim(),
    qrDataUrl: String(item.qrDataUrl || '').trim(),
    isPublished: Boolean(item.isPublished),
    experienceId: String(item.experienceId || '').trim(),
    experienceName: String(item.experienceName || '').trim(),
    ownerUsername: String(item.ownerUsername || '').trim(),
    durationSeconds: item.durationSeconds ?? null,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

export async function listAudioguideCatalog(): Promise<{ episodes: AudioguideItem[]; playlists: PodcastPlaylist[] }> {
  const payload = await requestJson<AudioguidesCatalogResponse>('/api/audioguides/catalog', { method: 'GET' });
  return {
    episodes: Array.isArray(payload.episodes) ? payload.episodes.map(normalizeEpisode) : [],
    playlists: Array.isArray(payload.playlists) ? payload.playlists.map(normalizePlaylist) : [],
  };
}

export async function listPlaylists(): Promise<PodcastPlaylist[]> {
  const payload = await listAudioguideCatalog();
  return payload.playlists;
}

export async function listAudioguides(): Promise<AudioguideItem[]> {
  const payload = await listAudioguideCatalog();
  return payload.episodes;
}

export async function createPlaylist(payload: PlaylistPayload): Promise<PodcastPlaylist> {
  const response = await requestJson<GenericResponse>('/api/audioguides/playlists/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.success) throw new Error(response.error || 'Creazione playlist non riuscita');
  return normalizePlaylist(response.playlist || { id: response.id || '', ...payload });
}

export async function updatePlaylist(
  id: string,
  payload: Partial<PlaylistPayload>,
): Promise<void> {
  const response = await requestJson<GenericResponse>('/api/audioguides/playlists/update', {
    method: 'POST',
    body: JSON.stringify({ id, ...payload }),
  });
  if (!response.success) throw new Error(response.error || 'Aggiornamento playlist non riuscito');
}

export async function deletePlaylist(id: string): Promise<void> {
  const response = await requestJson<GenericResponse>('/api/audioguides/playlists/delete', {
    method: 'POST',
    body: JSON.stringify({ id }),
  });
  if (!response.success) throw new Error(response.error || 'Eliminazione playlist non riuscita');
}

export async function createAudioguide(payload: AudioguidePayload): Promise<AudioguideItem> {
  const response = await requestJson<GenericResponse>('/api/audioguides/episodes/create', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.success) throw new Error(response.error || 'Creazione episodio non riuscita');
  const links = buildLinks(response.id || '');
  return normalizeEpisode(response.episode || { id: response.id || '', ...payload, ...links });
}

export async function updateAudioguide(
  id: string,
  payload: Partial<AudioguidePayload>,
): Promise<void> {
  const response = await requestJson<GenericResponse>('/api/audioguides/episodes/update', {
    method: 'POST',
    body: JSON.stringify({ id, ...payload }),
  });
  if (!response.success) throw new Error(response.error || 'Aggiornamento episodio non riuscito');
}

export async function deleteAudioguide(id: string): Promise<void> {
  const response = await requestJson<GenericResponse>('/api/audioguides/episodes/delete', {
    method: 'POST',
    body: JSON.stringify({ id }),
  });
  if (!response.success) throw new Error(response.error || 'Eliminazione episodio non riuscita');
}
